/**
 * Rattachement des visuels extraits d'images (liste plate, sans référence par
 * question) au bloc `statement` d'un exercice V2.
 *
 * `extract-visual-blocks` renvoie une liste de visuels SANS information de
 * placement par question. On les pose donc tous sur la SITUATION (bloc
 * `statement`, section `statement`) — affichée une fois en tête de l'énoncé. Le
 * tuteur peut ensuite les déplacer sur la bonne question dans l'éditeur V2
 * (`VisualsEditorPanel`).
 *
 * Module PUR (pas d'I/O) → unit-testable.
 */

import type { ExerciseVisualBlock } from '@jeraisonne/ai';
import type { BlockMutationOp, ExerciseBlock } from './types';

/**
 * Combine les visuels existants du bloc `statement` avec les nouveaux,
 * réattribue un `order` séquentiel et garantit l'unicité des `id` (collisions
 * possibles quand plusieurs images repartent chacune de l'id `t1`/`g1`…).
 */
export function normalizeStatementVisuals(
  existing: ExerciseVisualBlock[],
  added: ExerciseVisualBlock[],
): ExerciseVisualBlock[] {
  const seen = new Set<string>();
  return [...existing, ...added].map((v, i) => {
    let id = typeof v.id === 'string' && v.id.length > 0 ? v.id : `v_${i + 1}`;
    while (seen.has(id)) id = `${id}_${i + 1}`;
    seen.add(id);
    return { ...v, id, order: i };
  });
}

/**
 * Construit l'op `update` qui pose les visuels extraits sur le bloc `statement`,
 * en préservant ceux déjà présents. Renvoie `[]` (no-op) s'il n'y a aucun visuel
 * à ajouter ou pas de bloc `statement` (cohérence pas encore lancée).
 */
export function buildStatementVisualsOps(
  blocks: ExerciseBlock[],
  visuals: ExerciseVisualBlock[],
): BlockMutationOp[] {
  if (visuals.length === 0) return [];

  const statement = blocks.find((b) => b.block_type === 'statement' && b.section === 'statement');
  if (!statement) return [];

  const existingRaw = (statement.data as { visuals?: unknown }).visuals;
  const existing: ExerciseVisualBlock[] = Array.isArray(existingRaw)
    ? (existingRaw as ExerciseVisualBlock[])
    : [];

  const normalized = normalizeStatementVisuals(existing, visuals);

  return [
    {
      op: 'update',
      id: statement.id,
      patch: { data: { ...statement.data, visuals: normalized } },
    },
  ];
}
